/* TagPage — SEO landing page for `/recipes/tag/:tag`.
 *
 * Each tag becomes a topical hub: e.g. `/recipes/tag/baking` lists every
 * public recipe carrying the "Baking" tag. Google indexes this URL as the
 * canonical "baking recipes" page on the site, and the CollectionPage +
 * ItemList JSON-LD makes each entry a clickable list item.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useBulkSelect, SelectModeButton, BulkRecipeGrid, BulkMoveBar, withoutIds, withTags } from "@/components/BulkMove";
import { useAuth } from "@/contexts/AuthContext";
import PageMeta from "@/components/PageMeta";
import { recipePath } from "@/lib/slug";

function injectTagSchema(canonicalTag, recipes, origin) {
  if (!Array.isArray(recipes) || recipes.length === 0) return undefined;
  const id = "rf-tag-jsonld";
  let s = document.getElementById(id);
  if (!s) {
    s = document.createElement("script");
    s.id = id;
    s.type = "application/ld+json";
    document.head.appendChild(s);
  }
  s.text = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: `${canonicalTag} recipes`,
    url: `${origin}/recipes/tag/${encodeURIComponent(canonicalTag.toLowerCase())}`,
    description: `${recipes.length} family ${canonicalTag.toLowerCase()} recipes preserved on Recipe Filer.`,
    inLanguage: "en",
    isPartOf: { "@type": "WebSite", name: "Recipe Filer", url: "https://recipefiler.com" },
    mainEntity: {
      "@type": "ItemList",
      name: `${canonicalTag} recipes`,
      numberOfItems: recipes.length,
      itemListElement: recipes.slice(0, 200).map((r, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        url: `${origin}${recipePath(r)}`,
        name: r.title,
      })),
    },
  });
  return () => { const el = document.getElementById(id); if (el) el.remove(); };
}

export default function TagPage() {
  const { tag } = useParams();
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const sel = useBulkSelect();
  const mine = (r) => !!user && r.owner_id === user.user_id;

  useEffect(() => {
    const faqs = data?.faqs || [];
    if (!faqs.length) return undefined;
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "rf-tag-faq-jsonld";
    el.text = JSON.stringify({
      "@context": "https://schema.org", "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
    });
    document.head.appendChild(el);
    return () => el.remove();
  }, [data]);

  useEffect(() => {
    setData(null);
    window.scrollTo(0, 0);
    api.get(`/directory/tag/${encodeURIComponent(tag)}`)
      .then((r) => setData(r.data))
      .catch(() => setData({ tag, count: 0, recipes: [] }));
  }, [tag]);

  useEffect(() => {
    if (!data?.recipes?.length) return undefined;
    return injectTagSchema(data.tag, data.recipes, window.location.origin);
  }, [data]);

  if (!data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="tag-loading">
        <PageMeta title={`${tag.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())} recipes from family kitchens`} canonical={`/recipes/tag/${encodeURIComponent(tag)}`} />
        <h1 className="sr-only">{tag.replace(/-/g, " ")} recipes</h1>
        <div className="w-8 h-8 border-2 border-rf-orange border-t-transparent rounded-full animate-spin" aria-hidden="true" />
      </div>
    );
  }

  const canonicalTag = data.tag || tag;
  const recipes = data.recipes || [];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-20">
      <PageMeta
        title={`${canonicalTag} recipes — ${recipes.length} from family kitchens`}
        description={data.intro ? data.intro.slice(0, 155) : `Browse ${recipes.length} ${canonicalTag.toLowerCase()} ${recipes.length === 1 ? "recipe" : "recipes"} from cooks around the world on Recipe Filer — scanned from real family cookbooks and free to read.`}
        ogImage={recipes[0]?.images?.[0]}
      />
      <div className="flex gap-5 mb-4">
        <Link to="/directory" className="text-sm font-bold text-rf-orange hover:underline">← All recipes</Link>
        <Link to="/tags" data-testid="tag-all-tags-link" className="text-sm font-bold text-rf-orange hover:underline">All tags</Link>
      </div>
      <p className="overline mb-3" data-testid="tag-overline">Tag</p>
      <h1 className="font-display text-5xl md:text-7xl font-black text-rf-ink mb-6 leading-[0.95]" data-testid="tag-heading">
        {canonicalTag} recipes
      </h1>
      {data.intro && (
        <p className="text-rf-ink text-lg leading-relaxed mb-6 max-w-3xl" data-testid="tag-intro">{data.intro}</p>
      )}
      <p className="text-rf-ink-soft text-lg mb-12 max-w-2xl">
        {recipes.length === 0
          ? `No public recipes tagged "${canonicalTag}" yet.`
          : `${recipes.length} ${recipes.length === 1 ? "recipe" : "recipes"} preserved by Recipe Filer cooks. Free to read, free to share.`}
      </p>

      {recipes.length > 0 ? (
        <>
          <div className="flex justify-end mb-4"><SelectModeButton sel={sel} show={recipes.some(mine)} /></div>
          <BulkRecipeGrid recipes={recipes} sel={sel} canSelect={mine} testid="tag-recipe-grid" className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" />
          <BulkMoveBar sel={sel} selectable={recipes.filter(mine)}
                       onTrashed={(res) => setData((d) => ({ ...d, recipes: withoutIds(res)(d.recipes || []) }))}
                       onTagged={(res) => setData((d) => ({ ...d, recipes: withTags(res)(d.recipes || []) }))} />
        </>
      ) : (
        <div className="card-recipe text-center py-12">
          <p className="text-rf-ink-soft mb-4">Try the full directory or browse by letter.</p>
          <Link to="/directory" className="btn-amber">Browse all recipes</Link>
        </div>
      )}
      {(data.faqs || []).length > 0 && (
        <section aria-labelledby="tag-faq-heading" className="mt-16 max-w-3xl" data-testid="tag-faqs">
          <h2 id="tag-faq-heading" className="font-display text-2xl font-black text-rf-ink mb-4">{canonicalTag} recipe questions</h2>
          <div className="divide-y divide-rf-ink/10 border-y border-rf-ink/10">
            {data.faqs.map((f, i) => (
              <details key={i} className="group py-4" data-testid={`tag-faq-${i}`} open={i === 0}>
                <summary className="cursor-pointer font-bold text-rf-ink list-none flex justify-between gap-4">
                  {f.q}<span className="text-rf-orange group-open:rotate-45 transition-transform" aria-hidden="true">+</span>
                </summary>
                <p className="mt-2 text-rf-ink-soft leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}
      {(data.related || []).length > 0 && (
        <section aria-labelledby="tag-related-heading" className="mt-16" data-testid="tag-related">
          <h2 id="tag-related-heading" className="font-display text-2xl font-black text-rf-ink mb-4">Cooks also browse</h2>
          <div className="flex flex-wrap gap-2">
            {data.related.map((t) => (
              <Link key={t.slug} to={`/recipes/tag/${encodeURIComponent(t.slug)}`} data-testid={`tag-related-${t.slug}`}
                className="inline-flex items-baseline gap-1.5 rounded-full border border-rf-ink/15 bg-white px-4 py-2 font-bold text-rf-ink hover:border-rf-orange hover:text-rf-orange transition-colors">
                {t.tag} <span className="text-xs text-rf-ink-soft font-semibold">{t.count}</span>
              </Link>
            ))}
            <Link to="/tags" className="inline-flex items-center px-4 py-2 font-bold text-rf-orange hover:underline">All tags →</Link>
          </div>
        </section>
      )}
    </div>
  );
}
